import { Tooltip } from "@material-ui/core";
import ApproverNameDialog from "features/activity/components/ApproverNameDialog/ApproverNameDialog";
import { hasPermission } from "components/Authorize/authorizeUtil";
import { permissions } from "components/Authorize/permissions/permissions";
import NotificationProvider from "components/NotificationProvider";
import { mechanicalWorkTaskStyle } from "features/mechanicalWork/mechanicalWorkTaskStyle";
import { useGetActivityStatusReasonsQuery, useGetAllActivitiesQuery, useUpdateActivityLogMutation } from "features/activity/activitySlice";
import { NavLink } from "react-router-dom/cjs/react-router-dom.min";
import { useGetClientsQuery } from "features/clients/clientSlice";
import { useGetLotsQuery } from "features/lots/lotSlice";
import { activityPermissionProfile } from "components/Authorize/permissionProfiles/activityPermissionProfile";
import Authorize from "components/Authorize";
import { arrayToObject } from "utils/object-util";
import { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { selectIsClient } from "features/user/userSlice";
import { useGetVendorsQuery } from 'features/vendor/vendorSlice';
import ActivityStatusAliasView from "features/activityStatusAlias/ActivityStatusAliasView";
import { IconInfo } from "icons";
import { permissionProfiles } from "components/Authorize/permissionProfiles";
import { activityCustomFieldSetup, activityStatusOrder, activityStatusReasons, activityStatuses } from "features/activity/activityConsts";
import { orderBy } from "lodash";
import moment from "moment";

const MechanicalWorkDetails = ({ activity, currentActivityItem }) => {

    const classes = mechanicalWorkTaskStyle();

    const [updateActivityLog, { isLoading }] = useUpdateActivityLogMutation();

    let { data: activities, isLoading: isLoadingActivities } = useGetAllActivitiesQuery();
    let { data: lots, isLoading: isLoadingLots } = useGetLotsQuery();
    let { data: clients, isLoading: isLoadingClients } = useGetClientsQuery();
    let { data: vendors, isLoading: isLoadingVendors } = useGetVendorsQuery();
    let { data: statusReasons, error: statusReasonsError, isLoading: isLoadingStatusReasons } = useGetActivityStatusReasonsQuery();

    const lotLookup = useMemo(() => arrayToObject(lots), [lots]);
    const clientLookup = useMemo(() => arrayToObject(clients), [clients]);
    const activityLookup = useMemo(() => arrayToObject(activities), [activities]);
    const vendorLookup = useMemo(() => arrayToObject(vendors), [vendors]);
    const reasonLookup = useMemo(() => arrayToObject(statusReasons), [statusReasons]);

    const isClient = useSelector(selectIsClient);

    const [status, setStatus] = useState(activity?.activityId);
    const [approverNameDialogOpen, setApproverNameDialogOpen] = useState(false);
    const [pendingStatus, setPendingStatus] = useState(null);
    const [approverName, setApproverName] = useState('');
    activities = orderBy(
        activities?.map(a => ({
            ...a,
            order: activityStatusOrder[a.id]
        })),
        'order'
    ) ?? [];

    useEffect(() => {
        setStatus(activity?.activityId)
    }, [activity?.activityId])


    const activityInfo = useMemo(() => ({
        ...activity,
        lot: lotLookup?.[activity?.lotId],
        client: clientLookup?.[activity?.vehicle?.clientId],
        status: activityLookup?.[activity?.activityId],
        vendor: vendorLookup?.[activity?.vendorId]
    }), [
        activity,
        lotLookup,
        clientLookup,
        activityLookup,
        vendorLookup
    ]);

    const doSaveStatus = async (val, nameOverride) => {
        let prevStatus = status;
        setStatus(val);
        const result = await updateActivityLog({
            ...activityInfo,
            activityDate: new Date(moment().format("MMM DD, yyyy, hh:mm a") + " UTC").toISOString(),
            status: val ?? activityInfo?.activityId,
            inventoryUsed: null,
            approverName: nameOverride ?? undefined
        });

        if (result.error) {
            NotificationProvider.error("Failed to update the activity");
            setStatus(prevStatus);
        } else {
            NotificationProvider.success("Successfully updated the activity");
        }
    };

    const onSaveAction = (val) => {
        const isL1 = status == activityStatuses.PendingClientDecision && parseInt(val) == activityStatuses.PendingApproval;
        const isL2 = status == activityStatuses.PendingClientApproval && parseInt(val) == activityStatuses.PricingApprovedByClient;

        if ((isL1 || isL2) && !isClient) {
            setPendingStatus(val);
            setApproverName('');
            setApproverNameDialogOpen(true);
            return;
        }
        doSaveStatus(val);
    };

    const onConfirmApproverName = async () => {
        setApproverNameDialogOpen(false);
        await doSaveStatus(pendingStatus, approverName);
        setPendingStatus(null);
        setApproverName('');
    };

    const onCancelApproverName = () => {
        setApproverNameDialogOpen(false);
        setPendingStatus(null);
        setApproverName('');
    };

    const statusValues = ((!currentActivityItem?.noCharge && !hasPermission(permissions.ACTIVITIES.MANAGE_PENDING_INVOICE.key)) ? activities?.filter(a => a.id !== activityStatuses.Completed && a.id !== activityStatuses.CompleteBilling && a.id !== activityStatuses.CompletedAndPaid) : activities) ?? [];

    return (
        <>
            <ApproverNameDialog
                open={approverNameDialogOpen}
                onConfirm={onConfirmApproverName}
                onCancel={onCancelApproverName}
                value={approverName}
                onChange={setApproverName}
                clientAccountId={activityInfo?.vehicle?.customerId}
            />
            <div className={classes.detailsGrid}>
                <div className={classes.detailCell}>
                    <div className={classes.detailCellLabel}>Work Order ID</div>
                    <div className={classes.detailCellValue}>{activityInfo?.workOrderId}</div>
                </div>
                <div className={classes.detailCell}>
                    <div className={classes.detailCellLabel}>Asset ID</div>
                    <div className={classes.detailCellValue}>{activityInfo?.vehicle?.assetId}</div>
                </div>
                <div className={classes.detailCell}>
                    <div className={classes.detailCellLabel}>VIN</div>
                    <div className={classes.detailCellValue}>
                        <NavLink exact to={`/assets/detail/${activityInfo?.vehicleId}`}>
                            {activityInfo?.vehicle?.descriptor}
                        </NavLink>
                    </div>
                </div>
                <Authorize profile={activityPermissionProfile.WORK_ORDER_PAGE_SAVE}>
                    <div className={classes.detailCell}>
                        <div className={classes.detailCellLabel}>Client</div>
                        <div className={classes.detailCellValue}>{activityInfo?.client?.name}</div>
                    </div>
                </Authorize>
                <div className={classes.detailCell}>
                    <div className={classes.detailCellLabel}>Lot</div>
                    <div className={classes.detailCellValue}>{activityInfo?.lot?.name}</div>
                </div>
                <Authorize profile={permissionProfiles.ACTIVITY.EDIT_ACTIVITY_INTERNAL_FIELDS}>
                    <div className={classes.detailCell}>
                        <div className={classes.detailCellLabel}>Vendor</div>
                        <div className={classes.detailCellValue}>{activityInfo?.vendor?.name}</div>
                    </div>
                </Authorize>
                <div className={classes.detailCell}>
                    <div className={classes.detailCellLabel}>Repair Order</div>
                    <div className={classes.detailCellValue}>{activityInfo?.repairOrder}</div>
                </div>
                <div className={classes.detailCell}>
                    <div className={classes.detailCellLabel}>Status</div>
                    <div className={classes.status}>
                        <Tooltip title={activityInfo?.status?.name ?? ''}>
                            <span className={classes.statusControl}>
                                <Authorize profile={activityPermissionProfile.WORK_ORDER_PAGE_FIELDS} readonlyComponent={<div><ActivityStatusAliasView activityIds={[activityInfo?.status?.id]} clientId={activityInfo?.vehicle?.clientId} readonly={true} /></div>}>
                                    <ActivityStatusAliasView
                                        activityIds={statusValues?.map(s => s.id)}
                                        clientId={activityInfo?.vehicle?.clientId}
                                        readonly={false}
                                        dropdownValues={statusValues}
                                        value={status}
                                        onChange={e => onSaveAction(parseInt(e))}
                                        label={''}
                                    />
                                </Authorize>
                            </span>
                        </Tooltip>
                        {activity?.reasonId ? <Tooltip title={reasonLookup[parseInt(activity?.reasonId)]?.reasonText}><span><IconInfo /></span></Tooltip> : ''}
                    </div>
                </div>
            </div>
        </>
    )
}

export default MechanicalWorkDetails;